import { useCallback, useMemo } from 'react';

import { useLeadsRegistryFilterData } from '../../../../hooks/useLeadsRegistryFilterData';
import { useLeadsRegistryScopedFilters } from '../../../../hooks/useLeadsRegistryFilters';
import { LEADS_REGISTRY_CHAT_SCOPE_OPTIONS } from '../../../../model/constants';

import type {
  LeadsRegistryChatScope,
  LeadsRegistryFilterScope,
} from '../../../../model/types';

// «Требует ответа» — пресет поверх обычных зон чатов: сбрасывает фильтры и
// оставляет единственный фильтр «Статус — Не отвеченные». Значение статуса
// приходит бэковым словарём (GetLeadsFilterData), статического id в коде нет,
// поэтому пункт появляется только когда словарь содержит такой статус.
export const NEEDS_REPLY_SCOPE = 'needsReply';

export type LeadsRegistryChatScopeValue =
  | LeadsRegistryChatScope
  | typeof NEEDS_REPLY_SCOPE;

interface UseLeadsRegistryChatScopeFilterResult {
  options: {
    value: LeadsRegistryChatScopeValue;
    label: string;
    disabled?: boolean;
  }[];
  value: LeadsRegistryChatScopeValue;
  onChange(value: LeadsRegistryChatScopeValue): void;
}

export const useLeadsRegistryChatScopeFilter = (
  filterScope: LeadsRegistryFilterScope
): UseLeadsRegistryChatScopeFilterResult => {
  const {
    chatScope,
    filters,
    onChatScopeChange,
    onCheckboxFilterChange,
    onResetFilters,
  } = useLeadsRegistryScopedFilters(filterScope);
  const { checkboxOptions, isFilterDataLoading } = useLeadsRegistryFilterData();

  const unansweredStatusId = useMemo(
    () =>
      // Пробелы схлопываются: на стендах статус называется и «Неотвеченные»,
      // и «Не отвеченные».
      checkboxOptions.statuses.find(({ label }) =>
        label.toLowerCase().replace(/\s/g, '').startsWith('неотвечен')
      )?.id ?? null,
    [checkboxOptions.statuses]
  );

  const isNeedsReplySelected =
    chatScope === 'all' &&
    unansweredStatusId !== null &&
    filters.statuses.length === 1 &&
    filters.statuses[0] === unansweredStatusId;

  // While the dictionary is still loading, the option is kept visible but
  // disabled instead of being omitted — omitting it made the segment pop in
  // only once `GetLeadsFilterData` resolved (e.g. right after a page reload),
  // which read as the filter "not showing up" in the registry.
  const options = useMemo<
    { value: LeadsRegistryChatScopeValue; label: string; disabled?: boolean }[]
  >(
    () => [
      ...LEADS_REGISTRY_CHAT_SCOPE_OPTIONS,
      ...(isFilterDataLoading || unansweredStatusId !== null
        ? [
            {
              value: NEEDS_REPLY_SCOPE,
              label: 'Требует ответа',
              disabled: isFilterDataLoading,
            } as const,
          ]
        : []),
    ],
    [isFilterDataLoading, unansweredStatusId]
  );

  const changeHandler = useCallback(
    (nextValue: LeadsRegistryChatScopeValue) => {
      if (nextValue === NEEDS_REPLY_SCOPE) {
        if (unansweredStatusId === null) return;

        // Хранилище фильтров модульное и обновляется синхронно, поэтому
        // сброс и установка статуса последовательными вызовами не теряются.
        onResetFilters();
        onCheckboxFilterChange('statuses', [unansweredStatusId]);
        return;
      }

      // Уход с пресета снимает его статусный фильтр, чтобы «Мои чаты» и
      // прочие зоны не оставались случайно отфильтрованы по «Не отвеченные».
      if (isNeedsReplySelected) {
        onCheckboxFilterChange('statuses', []);
      }
      onChatScopeChange(nextValue);
    },
    [
      isNeedsReplySelected,
      onChatScopeChange,
      onCheckboxFilterChange,
      onResetFilters,
      unansweredStatusId,
    ]
  );

  return {
    options,
    value: isNeedsReplySelected ? NEEDS_REPLY_SCOPE : chatScope,
    onChange: changeHandler,
  };
};
